import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import SeoHead from '@/components/base/SeoHead';
import { getSiteUrl } from '@/utils/siteUrl';
import { getArticleBySlug, type ArticleData } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import ArticleContent from './components/ArticleContent';

const siteUrl = getSiteUrl();

function buildArticleJsonLd(article: ArticleData): Record<string, unknown> {
  const articleUrl = `${siteUrl}/article/${article.slug}`;
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: `${siteUrl}/`,
        name: 'MulebuySpreadsheets',
        description: 'Your trusted hub for verified mulebuy spreadsheet resources, agent comparisons, and sourcing guides.',
        inLanguage: 'en',
      },
      {
        '@type': 'Article',
        '@id': `${articleUrl}#article`,
        headline: article.title,
        description: article.subtitle,
        url: articleUrl,
        datePublished: article.publishedDate,
        dateModified: '2026-06-22',
        author: {
          '@type': 'Organization',
          name: 'MulebuySpreadsheets',
          url: `${siteUrl}/`,
        },
        publisher: {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'MulebuySpreadsheets',
          url: `${siteUrl}/`,
        },
        image: article.heroImage,
        inLanguage: 'en',
        isPartOf: { '@id': `${siteUrl}/#website` },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${siteUrl}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.title,
              item: articleUrl,
            },
          ],
        },
      },
    ],
  };

  if (article.slug === 'mulebuy-spreadsheet-faq') {
    (jsonLd['@graph'] as Record<string, unknown>[]).push({
      '@type': 'FAQPage',
      '@id': `${articleUrl}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a mulebuy spreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A mulebuy spreadsheet is a structured data file used to organize product listings, pricing, and agent comparisons for sourcing through Mulebuy platforms.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I choose the best mulebuy spreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Look for regularly updated spreadsheets with verified agent data, clear comparison columns, and positive user reviews. Our comparison guides help you evaluate options based on update frequency and feature sets.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are mulebuy spreadsheets safe to use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, when sourced from reputable providers. Always verify data independently before making purchase decisions, and never share personal financial information through spreadsheet links.',
          },
        },
        {
          '@type': 'Question',
          name: 'How often are mulebuy spreadsheet lists updated?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Update frequency varies by provider — some offer daily updates, others weekly or bi-weekly. Our resources section lists update schedules for each spreadsheet so you can choose one that matches your needs.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I use a mulebuy spreadsheet as a beginner?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. Many spreadsheets are designed with beginners in mind, featuring intuitive layouts and step-by-step guides. Check our Beginner Guide for a full walkthrough of getting started.',
          },
        },
      ],
    });
  }

  return jsonLd;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <main className="pt-24 pb-20">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">Article Not Found</h1>
          <p className="mt-4 text-foreground-600">The article you are looking for does not exist.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <>
      <SeoHead jsonLd={buildArticleJsonLd(article)} />
      <main>
        <article>
          <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50 overflow-hidden">
            <div className="absolute inset-0 z-0">
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/40 to-background-50"></div>
            </div>

            <div className="relative z-10 w-full px-4 md:px-6 max-w-4xl mx-auto">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer mb-6"
              >
                <i className="ri-arrow-left-line"></i> Back to Home
              </Link>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                {article.subtitle}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Published: {article.publishedDate}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>Category: {article.category}</span>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <ArticleContent slug={slug!} />

              <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">Ready to Start Sourcing?</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  Explore our curated <strong>mulebuy spreadsheet</strong> resources and start making smarter purchasing decisions today. Visit the main platform for direct access to all product categories and verified agent data.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-home-4-line"></i> Back to Homepage
                  </Link>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-white font-semibold text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Access Kaspreadsheet <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>

              <div className="mt-10">
                <h4 className="text-sm font-semibold text-foreground-800 mb-4">Explore More Guides:</h4>
                <div className="flex flex-wrap gap-3">
                  <Link to="/" className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    Mulebuy Spreadsheet Home
                  </Link>
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    Visit Main Platform
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
    </>
  );
}